import React from 'react';
import HScrollRow from '../HScrollRow';
import SectionTitle, { HomePinButton } from '../SectionTitle';
import AlbumCoverCard, { CARD_SIZE } from '../AlbumCoverCard';
import { dedupeRecentAlbumsById, filterRenderableRecentAlbums } from '../../store/recentAlbumsStore';
import { DEFAULT_HOME_SECTION_ORDER } from '../../config/homeSections';
import { favoritesForRow } from './favoritesRowLimit';
import { recentAlbumArtworkUrl } from '../../utils/recentAlbumArtworkUrl';
import { recentPlayCardFields, recentLocalClickAlbum } from '../../utils/recentPlayRoute';

const GOLD = '#C9A84C';
/** Section metadata — default order matches DEFAULT_HOME_SECTION_ORDER. */
export const HOME_SECTIONS = [
  { id: 'my_stations', title: 'My Stations', gold: true },
  { id: 'recently_played', title: 'Recently Played', gold: false },
  { id: 'favorites', title: 'Favorite Albums', gold: true },
  { id: 'picked_for_you', title: 'Picked for you', gold: true },
  { id: 'new_releases', title: 'New Releases', gold: false },
  { id: 'editors_picks', title: "Editor's Picks", gold: false },
  { id: 'featured_playlists', title: 'Featured Playlists', gold: false },
  { id: 'popular_genre', title: null, gold: false },
];

export function buildHomeSections(ctx) {
  const {
    SkeletonRow,
    HomeStationCard,
    FavSortDropdown,
    navigate,
    setShareTarget,
    myStations,
    recentAlbumsArr,
    ipcCovers,
    expandedSections,
    toggleSection,
    favAlbums,
    favSort,
    handleFavSort,
    removingFavIds,
    setRemovingFavIds,
    onRemoveFavorite,
    pickedForYou,
    newReleases,
    editorPicks,
    featuredPlaylists,
    onOpenPlaylist,
    popularGenreName,
    popularGenre,
    handleAlbumClick,
  } = ctx;
  return [
    {
      id: 'my_stations',
      isVisible: myStations !== null && myStations.length > 0,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 50ms both' }}>
          <SectionTitle title="My Stations" gold onPinToTop={onPinToTop} />
          <HScrollRow>
            {myStations.map((station, i) => (
              <HomeStationCard
                key={station.id}
                station={station}
                onClick={(s) => navigate(`/app/ella-radio?play=${s.id}`)}
                onShare={(s) => setShareTarget({ type: 'station', id: s.id, name: s.name || 'Station', artwork: s.imageUrl || null })}
                animDelay={i * 50}
              />
            ))}
          </HScrollRow>
        </div>
      ),
    },
    {
      id: 'recently_played',
      isVisible: filterRenderableRecentAlbums(recentAlbumsArr).length > 0,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 80ms both' }}>
          <SectionTitle
            title="Recently Played"
            expanded={expandedSections.recent}
            onToggle={recentAlbumsArr.length > 5 ? () => toggleSection('recent') : undefined}
            onPinToTop={onPinToTop}
          />
          <HScrollRow expanded={expandedSections.recent} cardSize={CARD_SIZE}>
            {dedupeRecentAlbumsById(recentAlbumsArr).slice(0, 25).map((a, i) => {
              const isLocal = a.source === 'local_library';
              const localCover = isLocal ? ipcCovers[a.album_id] : null;
              const remoteArt = isLocal ? null : recentAlbumArtworkUrl(a);
              const card = {
                id: a.album_id,
                title: a.title || '',
                artistName: a.artist || '',
                coverUrl: (isLocal ? (localCover || null) : (localCover || remoteArt)) || null,
                ...recentPlayCardFields(isLocal, a.album_id, a.source),
              };
              const clickAlbum = isLocal
                ? recentLocalClickAlbum(a, card.coverUrl)
                : {
                  id: a.album_id,
                  serviceAlbumId: a.album_id,
                  title: a.title,
                  artistName: a.artist || '',
                  coverUrl: card.coverUrl,
                  source: a.source || 'qobuz',
                };
              return (
                <AlbumCoverCard
                  key={`${a.album_id || 'recent'}-${i}`}
                  album={card}
                  onClick={() => handleAlbumClick(clickAlbum)}
                  animDelay={i * 40}
                />
              );
            })}
          </HScrollRow>
        </div>
      ),
    },
    {
      id: 'favorites',
      isVisible: favAlbums.length > 0,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 120ms both' }}>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: '-0.02em', margin: 0 }}>
              Favorite Albums
            </h2>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{favAlbums.length}</span>
            <div style={{ flex: 1 }} />
            <HomePinButton onPinToTop={onPinToTop} />
            {favAlbums.length > 1 && <FavSortDropdown value={favSort} onChange={handleFavSort} />}
            {favAlbums.length > 5 && (
              <button
                onClick={() => toggleSection('favorites')}
                title={expandedSections.favorites ? 'Collapse to row' : 'Expand to grid'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6,
                  color: expandedSections.favorites ? GOLD : 'var(--text-tertiary)', display: 'flex', alignItems: 'center',
                  transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; e.currentTarget.style.background = 'rgba(201,168,76,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = expandedSections.favorites ? GOLD : 'var(--text-tertiary)'; e.currentTarget.style.background = 'none'; }}
              >
                {expandedSections.favorites ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                  </svg>
                )}
              </button>
            )}
          </div>
          {favSort === 'artist' && expandedSections.favorites ? (
            (() => {
              let lastArtist = '';
              return (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_SIZE}px, 1fr))`,
                  gap: 16,
                }}>
                  {favAlbums.map((a, i) => {
                    const artist = a.artistName || '';
                    const showHeader = artist !== lastArtist;
                    lastArtist = artist;
                    const isRemoving = removingFavIds.has(a.id);
                    return (
                      <React.Fragment key={a.id || i}>
                        {showHeader && (
                          <div style={{ gridColumn: '1 / -1', paddingTop: i > 0 ? 16 : 0, paddingBottom: 4 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: GOLD, letterSpacing: '-0.01em' }}>{artist || 'Unknown Artist'}</span>
                          </div>
                        )}
                        <div
                          className="relative group/fav"
                          style={{
                            transition: 'opacity 0.3s ease, transform 0.3s ease',
                            opacity: isRemoving ? 0 : 1,
                            transform: isRemoving ? 'scale(0.85)' : 'scale(1)',
                          }}
                        >
                          <AlbumCoverCard album={a} onClick={() => handleAlbumClick(a)} animDelay={i * 40} />
                          {onRemoveFavorite && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRemovingFavIds((prev) => new Set(prev).add(a.id));
                                setTimeout(() => onRemoveFavorite(a), 320);
                              }}
                              className="absolute top-2 right-2 z-10 opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover/fav:opacity-100 transition-opacity touch-manipulation"
                              style={{
                                width: 28, height: 28, borderRadius: '50%',
                                background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#fff',
                              }}
                              title="Remove from favorites"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <HScrollRow expanded={expandedSections.favorites} cardSize={CARD_SIZE}>
              {favoritesForRow(favAlbums, expandedSections.favorites).map((a, i) => {
                const isRemoving = removingFavIds.has(a.id);
                return (
                  <div
                    key={a.id || i}
                    className="flex-shrink-0 relative group/fav"
                    style={{
                      width: CARD_SIZE,
                      transition: 'opacity 0.3s ease, transform 0.3s ease',
                      opacity: isRemoving ? 0 : 1,
                      transform: isRemoving ? 'scale(0.85)' : 'scale(1)',
                    }}
                  >
                    <AlbumCoverCard album={a} onClick={() => handleAlbumClick(a)} animDelay={i * 40} />
                    {onRemoveFavorite && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemovingFavIds((prev) => new Set(prev).add(a.id));
                          setTimeout(() => onRemoveFavorite(a), 320);
                        }}
                        className="absolute top-2 right-2 z-10 opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover/fav:opacity-100 transition-opacity touch-manipulation"
                        style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', color: '#fff',
                        }}
                        title="Remove from favorites"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </HScrollRow>
          )}
        </div>
      ),
    },
    {
      id: 'picked_for_you',
      isVisible: true,
      render: (onPinToTop) => (
        pickedForYou === null ? (
          <div style={{ marginBottom: 36 }}>
            <SectionTitle title="Picked for you" gold onPinToTop={onPinToTop} />
            <SkeletonRow />
          </div>
        ) : (
          pickedForYou.map((group, gIdx) => (
            <div key={group.genre} style={{ marginBottom: 36, animation: `homeFadeIn 0.4s ease ${gIdx * 100 + 100}ms both` }}>
              <SectionTitle
                title={group.label}
                gold
                expanded={expandedSections[`genre_${group.genre}`]}
                onToggle={group.albums.length > 5 ? () => toggleSection(`genre_${group.genre}`) : undefined}
                onPinToTop={gIdx === 0 ? onPinToTop : undefined}
              />
              <HScrollRow expanded={expandedSections[`genre_${group.genre}`]} cardSize={CARD_SIZE}>
                {group.albums.map((a, i) => (
                  <AlbumCoverCard key={a.id || i} album={a} onClick={() => handleAlbumClick(a)} animDelay={i * 50} />
                ))}
              </HScrollRow>
            </div>
          ))
        )
      ),
    },
    {
      id: 'new_releases',
      isVisible: true,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 200ms both' }}>
          <SectionTitle
            title="New Releases"
            expanded={expandedSections.newReleases}
            onToggle={newReleases && newReleases.length > 5 ? () => toggleSection('newReleases') : undefined}
            onPinToTop={onPinToTop}
          />
          {newReleases === null ? <SkeletonRow /> : (
            <HScrollRow expanded={expandedSections.newReleases} cardSize={CARD_SIZE}>
              {newReleases.map((a, i) => (
                <AlbumCoverCard key={a.id || i} album={a} onClick={() => handleAlbumClick(a)} animDelay={i * 30} />
              ))}
            </HScrollRow>
          )}
        </div>
      ),
    },
    {
      id: 'editors_picks',
      isVisible: true,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 300ms both' }}>
          <SectionTitle title="Editor's Picks" expanded onPinToTop={onPinToTop} />
          {editorPicks === null ? <SkeletonRow /> : (
            <HScrollRow expanded cardSize={CARD_SIZE}>
              {editorPicks.map((a, i) => (
                <AlbumCoverCard key={a.id || i} album={a} onClick={() => handleAlbumClick(a)} animDelay={i * 30} />
              ))}
            </HScrollRow>
          )}
        </div>
      ),
    },
    {
      id: 'featured_playlists',
      isVisible: featuredPlaylists !== null && featuredPlaylists.length > 0,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 400ms both' }}>
          <SectionTitle title="Featured Playlists" onPinToTop={onPinToTop} />
          <HScrollRow>
            {featuredPlaylists.map((pl, i) => {
              const plCover = pl.images300?.[0] || pl.images?.[0] || pl.images150?.[0] || pl.image?.large || null;
              const name = pl.name || '';
              const count = pl.tracks_count || 0;
              return (
                <button
                  key={pl.id || i}
                  type="button"
                  onClick={() => onOpenPlaylist?.(pl)}
                  className="flex-shrink-0 text-left group touch-manipulation select-none [-webkit-tap-highlight-color:transparent]"
                  style={{ width: 280, animation: `homeFadeIn 0.4s ease ${i * 50}ms both` }}
                >
                  <div
                    className="rounded-xl overflow-hidden transition-transform duration-200 pointer-fine:group-hover:scale-[1.03]"
                    style={{
                      width: 280, height: 150,
                      background: '#f5f5f7', border: '1px solid #eee',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    }}
                  >
                    {plCover ? (
                      <img src={plCover} alt={name} className="w-full h-full object-cover pointer-events-none" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ color: '#ccc', fontSize: 40 }}>🎶</div>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    {count > 0 && <div style={{ fontSize: 12, color: '#86868b', marginTop: 2 }}>{count} tracks</div>}
                  </div>
                </button>
              );
            })}
          </HScrollRow>
        </div>
      ),
    },
    {
      id: 'popular_genre',
      isVisible: true,
      render: (onPinToTop) => (
        <div style={{ marginBottom: 36, animation: 'homeFadeIn 0.4s ease 500ms both' }}>
          <SectionTitle title={`Popular in ${popularGenreName}`} onPinToTop={onPinToTop} />
          {popularGenre === null ? <SkeletonRow /> : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_SIZE}px, 1fr))`,
              gap: 20,
            }}>
              {popularGenre.map((a, i) => (
                <AlbumCoverCard key={a.id || i} album={a} onClick={() => handleAlbumClick(a)} animDelay={i * 40} />
              ))}
            </div>
          )}
        </div>
      ),
    },
  ].filter(s => DEFAULT_HOME_SECTION_ORDER.includes(s.id));
}
